import React from "react";

const MERCHANT_BANNER_SLIDES = [
  {
    key: "en",
    src: process.env.PUBLIC_URL + "/merchant-sidebar-banner-en.png",
    alt: "ANEXT Bank credit line banner in English",
  },
  {
    key: "cn",
    src: process.env.PUBLIC_URL + "/merchant-sidebar-banner-cn.png",
    alt: "ANEXT Bank credit line banner in Chinese",
  },
];

const MERCHANT_BANNER_INTERVAL_MS = 4500;
const MERCHANT_BANNER_TRANSITION_MS = 650;

export default function MerchantSidebarBanner({ linkPath, onClick }) {
  const displaySlides =
    MERCHANT_BANNER_SLIDES.length > 1
      ? MERCHANT_BANNER_SLIDES.concat(MERCHANT_BANNER_SLIDES[0])
      : MERCHANT_BANNER_SLIDES;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(true);

  const resetToFirstBanner = React.useCallback(function resetToFirstBanner() {
    setIsAnimating(false);
    setActiveIndex(0);
  }, []);

  React.useEffect(function rotateMerchantBanner() {
    if (MERCHANT_BANNER_SLIDES.length <= 1) {
      return undefined;
    }

    const timer = window.setInterval(function showNextBanner() {
      setIsAnimating(true);
      setActiveIndex(function getNextIndex(currentIndex) {
        if (currentIndex >= MERCHANT_BANNER_SLIDES.length) {
          return currentIndex;
        }

        return currentIndex + 1;
      });
    }, MERCHANT_BANNER_INTERVAL_MS);

    return function cleanup() {
      window.clearInterval(timer);
    };
  }, []);

  React.useEffect(
    function restoreBannerTransition() {
      if (isAnimating) {
        return undefined;
      }

      let firstFrame = 0;
      let secondFrame = 0;

      firstFrame = window.requestAnimationFrame(function waitForResetFrame() {
        secondFrame = window.requestAnimationFrame(function enableTransition() {
          setIsAnimating(true);
        });
      });

      return function cleanup() {
        window.cancelAnimationFrame(firstFrame);
        window.cancelAnimationFrame(secondFrame);
      };
    },
    [isAnimating]
  );

  React.useEffect(
    function resetClonedBannerFallback() {
      if (activeIndex < MERCHANT_BANNER_SLIDES.length) {
        return undefined;
      }

      const resetTimer = window.setTimeout(function resetAfterTransition() {
        resetToFirstBanner();
      }, MERCHANT_BANNER_TRANSITION_MS + 100);

      return function cleanup() {
        window.clearTimeout(resetTimer);
      };
    },
    [activeIndex, resetToFirstBanner]
  );

  function handleTransitionEnd(event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (activeIndex === MERCHANT_BANNER_SLIDES.length) {
      resetToFirstBanner();
    }
  }

  const visibleSlideIndex = Math.min(activeIndex, displaySlides.length - 1);

  return (
    <a
      className="merchant-sidebar-banner-link"
      href={linkPath}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      <span
        className="merchant-sidebar-banner-track"
        onTransitionEnd={handleTransitionEnd}
        style={{
          transform: "translateX(-" + visibleSlideIndex * 100 + "%)",
          transition: isAnimating ? undefined : "none",
        }}
      >
        {displaySlides.map(function renderSlide(slide, index) {
          return (
            <span
              className="merchant-sidebar-banner-slide"
              key={slide.key + "-" + index}
              aria-hidden={index >= MERCHANT_BANNER_SLIDES.length}
            >
              <img
                className="merchant-sidebar-banner-image"
                src={slide.src}
                alt={slide.alt}
              />
            </span>
          );
        })}
      </span>
    </a>
  );
}
